import { ReportFilters, ReportSortBy, ReportSortDir, useReportData } from '../../../contexts/ReportsContextProvider'
import styles from './SponsoredReport.module.css'
import { t } from 'i18next'
import { useCallback, useMemo, useRef, useState } from 'react'
import { getIcon } from '../../../common/components/icons/utils'
import { SponsoredDownloader, SponsoredReportGame } from '../../../contexts/ReportsContextHelper'
import { PaginationSelectOption } from '../../../composites/GamesOverview/components/PaginationControls'
import { SingleValue } from 'react-select'
import { ReportFiltersForm } from './ReportFiltersForm'
import { DAY, EMPTY_GRAPH, getDayGraphData, getMonthGraphData, NOW, OrgReportInsights, YEAR } from './ReportHelper'
import { BarGraph, createPDFfromHTML, SummaryDataBox } from './ReportCommon'
import reportStyles from './ReportCommon.module.css'
import { SponsoredReportingGameCard } from '../../../composites/GamesOverview/components/card/ReportingSponsoredGameCard'
import { Select, SelectOption, SelectVariant } from '../../../common/components/Select/Select'
import { CellElement, Table, TableRow } from './GenericTable'
import classNames from 'classnames'
import { Button } from '../../../common/components/button/Button'
import { getOldUIRoutes } from '../../../routes'
import { dateTimeToDashDateString, parseDDMMYYYYcomma } from '../../../util/string'

export const SponsoredReport: React.FC = () => {
  const {
    sponsoredReportData,
    sponsoredDailyPlayers,
    fetchSponsoredGameData,
    setFilters,
    currentPage,
    lastPage,
    pageData,
    pageSize,
    allPageOptions,
    selectPageSize,
    setPage,
    setSort,
    next,
    prev,
    loading,
  } = useReportData()
  const [selectedGame, setSelectedGame] = useState<SponsoredReportGame>()
  const [insightText] = useState(OrgReportInsights()[Math.floor(Math.random() * OrgReportInsights().length)])
  const [showInsightBox, setShowInsightBox] = useState(true)
  const [graphStartDate, setGraphStartDate] = useState(new Date(new Date(NOW() - YEAR).setDate(1)))
  const [graphEndDate, setGraphEndDate] = useState(new Date(NOW()))
  const [internalFilters, setInternalFilters] = useState<ReportFilters>({})
  const [ratingSortDir, setRatingSortDir] = useState<string>()
  const cancelGameDataFetch = useRef<number[]>([])
  const [hideShadows, setHideShadows] = useState(false)
  const [activeSortCol, setActiveSortCol] = useState<ReportSortBy>()
  const [activeSortDir, setActiveSortDir] = useState<ReportSortDir>()
  const [rangeSet, setRangeSet] = useState(false)

  const handleDownloadPDF = useCallback(() => {
    if (selectedGame) {
      setHideShadows(true)
      setTimeout(() => {
        createPDFfromHTML('PDF_DIV', selectedGame?.gameName)
        setHideShadows(false)
      }, 25)
    }
  }, [selectedGame])

  const handleSort = useCallback(
    (sortKey: string) => (direction: ReportSortDir | string) => {
      if (setSort) {
        setActiveSortCol(sortKey as ReportSortBy)
        setActiveSortDir(direction as ReportSortDir)
        setSort(sortKey as ReportSortBy, direction as ReportSortDir)
      }
    },
    [setSort],
  )

  const ratingSortOptions = useMemo((): SelectOption[] => {
    return [
      {
        value: 'DESC',
        label: t('reports.sort_ratings_option.highest_first', 'Highest first'),
      },
      {
        value: 'ASC',
        label: t('reports.sort_ratings_option.lowest_first', 'Lowest first'),
      },
    ]
  }, [])

  const onChangePageSize = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      selectPageSize(option!.value)
    },
    [selectPageSize],
  )

  const onChangeCurrentPage = useCallback(
    (option: SingleValue<PaginationSelectOption>) => {
      setPage(option!.value)
    },
    [setPage],
  )

  const handleCloseInsightBox = useCallback(() => {
    setShowInsightBox(false)
  }, [])

  const handleOpenGameDetails = useCallback(
    async (game: SponsoredReportGame, createdAtFrom?: string, createdAtUntil?: string) => {
      setSelectedGame(game)
      window.scrollTo({ top: 0 })
      cancelGameDataFetch.current = cancelGameDataFetch.current.filter((e) => e !== game.conlibGameId)
      if (fetchSponsoredGameData) {
        const moreGameData = await fetchSponsoredGameData(game.conlibGameId, createdAtFrom, createdAtUntil)
        if (moreGameData && cancelGameDataFetch.current.indexOf(game.conlibGameId) < 0) {
          const merged = JSON.parse(JSON.stringify(game))
          Object.keys(moreGameData).forEach((key) => {
            if (moreGameData[key as keyof SponsoredReportGame]) {
              merged[key as keyof SponsoredReportGame] = moreGameData[key as keyof SponsoredReportGame]
            }
          })
          setSelectedGame(merged)
        }
      }
    },
    [fetchSponsoredGameData],
  )

  const setFiltersInternal = useCallback(
    (filters: ReportFilters) => {
      if (selectedGame) {
        // handleOpenGameDetails(selectedGame, filters.createdAtFrom, filters.createdAtUntil)
      }
      setInternalFilters(filters)
      if (filters.createdAtFrom) {
        setGraphStartDate(new Date(filters.createdAtFrom))
      } else {
        setGraphStartDate(new Date(new Date(NOW() - YEAR).setDate(1)))
      }
      if (filters.createdAtUntil) {
        setGraphEndDate(new Date(filters.createdAtUntil))
      } else {
        setGraphEndDate(new Date(NOW()))
      }
      setRangeSet(!!filters.createdAtFrom || !!filters.createdAtUntil)
      setFilters(filters)
    },
    [selectedGame, setFilters],
  )

  const downloadsGraphData = useMemo(() => {
    if (!selectedGame || !selectedGame.downloadsByDays) {
      return EMPTY_GRAPH
    }
    const daysInRange = (graphEndDate.getTime() - graphStartDate.getTime()) / DAY

    if (daysInRange > 31) {
      return getMonthGraphData(selectedGame.downloadsByDays, graphStartDate, graphEndDate)
    } else {
      return getDayGraphData(selectedGame.downloadsByDays, graphStartDate, graphEndDate)
    }
  }, [graphEndDate, graphStartDate, selectedGame])

  const playersGraphData = useMemo(() => {
    if (!selectedGame || !sponsoredDailyPlayers) {
      return EMPTY_GRAPH
    }
    const daysInRange = (graphEndDate.getTime() - graphStartDate.getTime()) / DAY

    if (daysInRange > 31) {
      return getMonthGraphData(sponsoredDailyPlayers, graphStartDate, graphEndDate)
    } else {
      return getDayGraphData(sponsoredDailyPlayers, graphStartDate, graphEndDate)
    }
  }, [graphEndDate, graphStartDate, selectedGame, sponsoredDailyPlayers])

  const onChangeSortDir = (sortDir: SingleValue<SelectOption>) => {
    if (sortDir?.value) setRatingSortDir(sortDir.value)
  }

  const selectedOption = ratingSortOptions.find((o) => o.value === ratingSortDir)

  const avarageStars = useMemo(() => {
    return selectedGame?.ratingDetails?.av_rating ?? 0
  }, [selectedGame?.ratingDetails?.av_rating])

  const sortedRatings = useMemo(() => {
    if (!selectedGame) return []
    if (ratingSortDir === 'ASC') {
      return selectedGame.ratingDetails?.details.sort((a, b) => a.value - b.value)
    } else if (ratingSortDir === 'DESC') {
      return selectedGame.ratingDetails?.details.sort((a, b) => b.value - a.value)
    } else {
      return selectedGame.ratingDetails?.details ?? []
    }
  }, [ratingSortDir, selectedGame])

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []
    pageData?.forEach((dataRow) => {
      const row = dataRow as SponsoredDownloader
      const tableRow: CellElement[] = [
        row.email,
        dateTimeToDashDateString(parseDDMMYYYYcomma(row.createdAt.toString())),
        row.organisation,
      ]
      rows.push(tableRow)
    })
    return rows
  }, [pageData])

  const handleDownloadXLS = useCallback(() => {
    if (selectedGame)
      window.location.href = getOldUIRoutes.sponsoredDownloadersReportExcel(
        selectedGame.conlibGameId,
        internalFilters.createdAtFrom,
        internalFilters.createdAtUntil,
      )
  }, [internalFilters.createdAtFrom, internalFilters.createdAtUntil, selectedGame])

  return (
    <div className={classNames(styles.container, hideShadows && styles.hideShadows)} id='PDF_DIV'>
      {!selectedGame ? (
        <>
          <div className={styles.headerRow}>
            <h1>{t('reports.sponsored.title', 'Sponsored games')}</h1>
          </div>
          {showInsightBox && (
            <div className={styles.insightsBox}>
              <div className={styles.insightsLeft}>
                <div className={styles.insightTitle}>{t('reports.insight_title', 'Insights')}</div>
                <div className={styles.basicGreyText}>{insightText}</div>
              </div>
              <div className={styles.insightsRight} onClick={handleCloseInsightBox}>
                <span className={styles.closeIcon}>{getIcon('close')}</span>
              </div>
            </div>
          )}
          <div className={styles.gamesGrid}>
            {sponsoredReportData?.games.map((game) => {
              return (
                <SponsoredReportingGameCard key={game.conlibGameId} game={game} onOpenGame={handleOpenGameDetails} />
              )
            })}
          </div>
        </>
      ) : (
        <>
          <div className={styles.headerRow}>
            <div
              onClick={() => {
                setSelectedGame(undefined)
                cancelGameDataFetch.current.push(selectedGame.conlibGameId)
              }}
              className={styles.goBack}
            >
              {getIcon('arrowLeft')}
            </div>
            <h1>{selectedGame.gameName}</h1>
            <Button className={styles.downloadPdfButton} variant='outline-normal' onClick={handleDownloadPDF}>
              {t('reports.download_pdf', 'Export as PDF')}
            </Button>
          </div>
          <SponsoredReportingGameCard game={selectedGame} hideDetails />
          <ReportFiltersForm onChange={setFiltersInternal} />
          <div className={reportStyles.summaryDataBoxesRow}>
            <SummaryDataBox
              iconName='calendar'
              value={new Date(selectedGame.createdAt + '').toLocaleDateString()}
              label={t('reports.created', 'Created')}
            />
            <SummaryDataBox
              iconName='changeIcon'
              value={new Date(selectedGame.updatedAt + '').toLocaleDateString()}
              label={t('report.updated', 'Updated')}
            />
            <SummaryDataBox
              iconName='multipleUsers'
              value={selectedGame.numberOfPlayers}
              label={t('reports.total_players', 'Total Players')}
            />
            <SummaryDataBox
              iconName='trophyOutline'
              value={selectedGame.numberOfDownloadedGames}
              label={t('reports.Downloads', 'Downloads')}
            />
          </div>

          <div>
            <h2>{t('reports.downloads_over_time', 'Downloads Over Time')}</h2>
            <div className={styles.basicGreyText}>
              {t(
                'reports.downloads_over_time_info',
                'Track when your sponsored game has been downloaded to identify interest peaks and usage trends.',
              )}
            </div>
          </div>
          <BarGraph graphData={downloadsGraphData} />

          <div>
            <h2>{t('reports.player_engagement', 'Player Engagement')}</h2>
            <div className={styles.basicGreyText}>
              {rangeSet
                ? t(
                    'reports.player_engagement_info_range',
                    'Track how many players have played your sponsored game over time to spot engagement trends and peak activity periods. Includes players joining games that were downloaded outside the selected date range.',
                  )
                : t(
                    'reports.player_engagement_info',
                    'Track how many players have played your sponsored game over time to spot engagement trends and peak activity periods.',
                  )}
            </div>
          </div>
          <BarGraph graphData={playersGraphData} />

          <div className={styles.headerRow}>
            <h2>{t('reports.customers', 'Customers')}</h2>
            <Button variant='outline-tiny' onClick={handleDownloadXLS}>
              {t('reports.download_excel', 'Export Excel')}
            </Button>
          </div>
          <Table
            isSortable
            hasPagination
            headerWidths={['40%', '20%', '40%']}
            headerLabels={[
              t('reports.table.email', 'Email'),
              t('reports.table.download_date', 'Download date'),
              t('reports.table.organization', 'Organization'),
            ]}
            headerSortTags={[ReportSortBy.SP_EMAIL, ReportSortBy.SP_CREATED, ReportSortBy.SP_ORGANISATION]}
            onSort={handleSort}
            activeSortCol={activeSortCol}
            activeSortDir={activeSortDir}
            rows={tableRowData}
            currentPage={currentPage}
            pageSize={pageSize}
            lastPage={lastPage}
            allPageOptions={allPageOptions}
            onClickNext={next}
            onClickPrev={prev}
            onChangePageSize={onChangePageSize}
            onChangeCurrentPage={onChangeCurrentPage}
            itemsPerPageLabel={t('reports.pagination.customers_per_page', 'customers per page')}
            loading={loading}
          />
          <h2>{t('reports.reviews', 'Reviews')}</h2>
          <div className={styles.reviewArea}>
            <div className={styles.ratingContainer}>
              <p className={styles.ratingNumber}>{avarageStars.toFixed(1)}</p>
              <div className={styles.ratedStars}>
                <div className={styles.ratedStar} key='first-star'>
                  {getIcon(avarageStars >= 1 ? 'star' : avarageStars >= 0.5 ? 'starHalf' : 'starOutline')}
                </div>
                <div className={styles.ratedStar} key='second-star'>
                  {getIcon(avarageStars >= 2 ? 'star' : avarageStars >= 1.5 ? 'starHalf' : 'starOutline')}
                </div>
                <div className={styles.ratedStar} key='third-star'>
                  {getIcon(avarageStars >= 3 ? 'star' : avarageStars >= 2.5 ? 'starHalf' : 'starOutline')}
                </div>
                <div className={styles.ratedStar} key='fourth-star'>
                  {getIcon(avarageStars >= 4 ? 'star' : avarageStars >= 3.5 ? 'starHalf' : 'starOutline')}
                </div>
                <div className={styles.ratedStar} key='fifth-star'>
                  {getIcon(avarageStars >= 5 ? 'star' : avarageStars >= 4.5 ? 'starHalf' : 'starOutline')}
                </div>
              </div>
              <p className={styles.ratingAmountText}>
                {avarageStars.toFixed(1) ?? 0} / 5 {'('}
                {selectedGame.ratingDetails?.details?.length ?? 0}{' '}
                {t('rating.rating_modal.ratings.ratings_amount', 'ratings')}
                {')'}
              </p>
              <div className={styles.ratingSortArea}>
                <span className={styles.ratingAmountText}>{t('reports.sort_reviews_by', 'Sort reviews by:')}</span>
                <Select
                  options={ratingSortOptions}
                  value={selectedOption}
                  onChange={onChangeSortDir}
                  className={styles.ratingsSortInput}
                  variant={SelectVariant.Small}
                />
              </div>
            </div>
            <div className={styles.allRatingsContainer}>
              {sortedRatings.map((rating, index) => {
                return (
                  <div className={styles.singleRatingContainer} key={`${index}-rating`}>
                    <div className={styles.singleRatingStars}>
                      <div className={styles.singleRatingStar} key={`${index}-single-first-star`}>
                        {getIcon(rating.value >= 1 ? 'star' : rating.value >= 0.5 ? 'starHalf' : 'starOutline')}
                      </div>
                      <div className={styles.singleRatingStar} key={`${index}-single-second-star`}>
                        {getIcon(rating.value >= 2 ? 'star' : rating.value >= 1.5 ? 'starHalf' : 'starOutline')}
                      </div>
                      <div className={styles.singleRatingStar} key={`${index}-single-third-star`}>
                        {getIcon(rating.value >= 3 ? 'star' : rating.value >= 2.5 ? 'starHalf' : 'starOutline')}
                      </div>
                      <div className={styles.singleRatingStar} key={`${index}-single-fourth-star`}>
                        {getIcon(rating.value >= 4 ? 'star' : rating.value >= 3.5 ? 'starHalf' : 'starOutline')}
                      </div>
                      <div className={styles.singleRatingStar} key={`${index}-single-fifth-star`}>
                        {getIcon(rating.value >= 5 ? 'star' : rating.value >= 4.5 ? 'starHalf' : 'starOutline')}
                      </div>
                    </div>
                    <p className={styles.ratingUser}>-{rating.user}</p>
                    {rating && rating.comment && rating.comment.length > 0 && <p>"{rating.comment}"</p>}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
